import React, { useState, useEffect } from 'react';
import { leadAPI, employeeAPI, roleAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LeadDetails from './LeadDetails';
import CallButton from './CallButton';
import NoteButton from './NoteButton';
import './LeadManagement.css';

const LeadManagement = () => {
  const { currentUser, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('all-leads');
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [viewingLead, setViewingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [creationMode, setCreationMode] = useState('single'); // 'single' or 'bulk'
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkData, setBulkData] = useState({
    mobilesText: '',
    leadSource: 'Call',
    priority: 'Medium',
  });
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    salesStage: '',
    priority: '',
    search: '',
    assignmentStatus: '',
  });

  const isPrivilegedUser = () => {
    if (!currentUser) return false;
    
    // Check permission helper from AuthContext (Super Admin has 'all')
    if (hasPermission && (hasPermission('all') || hasPermission('employee_create') || hasPermission('lead_assign') || hasPermission('lead_view'))) {
      const roleVal = currentUser.role;
      const normRole = typeof roleVal === 'string' ? roleVal.toLowerCase() : (roleVal?.id || roleVal?.name || '').toLowerCase();
      // Sales executives/employees shouldn't have privileged access even if they have lead_view
      if (normRole === 'employee' || normRole === 'sales') {
        return false;
      }
      return true;
    }
    
    const roleValue = currentUser.role;
    let normalizedRole = '';
    if (typeof roleValue === 'string') {
      normalizedRole = roleValue.toLowerCase();
    } else if (roleValue && typeof roleValue === 'object') {
      normalizedRole = (roleValue.id || roleValue.name || roleValue._id || '').toLowerCase();
    }
    
    const designation = (currentUser.designation || '').toLowerCase();
    const dept = (currentUser.department || '').toLowerCase();
    
    return normalizedRole === 'admin' || 
           normalizedRole === 'super_admin' || 
           normalizedRole === 'super admin' || 
           normalizedRole === 'administrator' || 
           normalizedRole === 'hr' || 
           normalizedRole === 'manager' ||
           designation.includes('admin') || 
           designation.includes('hr') || 
           designation.includes('manager') ||
           designation.includes('director') || 
           designation.includes('vp') || 
           designation.includes('president') ||
           dept.includes('hr') ||
           dept.includes('management');
  };

  // Form state for new lead
  const [formData, setFormData] = useState({
    customerName: '',
    mobile: '',
    email: '',
    alternatePhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
    },
    leadSource: 'Walk-in',
    leadStatus: 'New',
    salesStage: 'Inquiry',
    dealValue: 0,
    probability: 0,
    productInterest: '',
    requirement: '',
    budget: { min: 0, max: 0 },
    timeline: 'Within 1 Month',
    priority: 'Medium',
  });

  // Assignment state
  const [assignmentData, setAssignmentData] = useState({
    selectedRole: '',
    selectedEmployee: '',
  });

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchRoles();
  }, [filters]);

  useEffect(() => {
    if (showAssignModal) {
      fetchAllSalesEmployees();
    }
  }, [showAssignModal]);

  const fetchAllSalesEmployees = async () => {
    try {
      const response = await employeeAPI.getAll();
      const allEmps = response.data || [];
      const salesEmps = allEmps.filter(emp => {
        const dept = (emp.department || '').toLowerCase();
        const desig = (emp.designation || '').toLowerCase();
        const roleVal = emp.role;
        const roleName = typeof roleVal === 'string' 
          ? roleVal.toLowerCase() 
          : (roleVal?.id || roleVal?.name || '').toLowerCase();
        
        return dept === 'sales' || 
               desig.includes('sales') || 
               roleName === 'sales';
      });
      setEmployees(salesEmps);
    } catch (error) {
      console.error('Error fetching sales employees:', error);
      setEmployees([]);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await leadAPI.getAll(filters);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      alert('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await leadAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await roleAPI.getAll();
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchEmployeesByRole = async (roleId) => {
    try {
      console.log('Fetching employees for role:', roleId);
      const response = await employeeAPI.getAll({ role: roleId });
      console.log('Employee API response:', response);
      console.log('Employees data:', response.data);
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (selectedLead) {
        await leadAPI.update(selectedLead._id, formData);
        alert('Lead updated successfully!');
      } else {
        if (creationMode === 'bulk') {
          if (bulkFile) {
            const fileData = new FormData();
            fileData.append('file', bulkFile);
            const response = await leadAPI.import(fileData);
            alert(response.data?.message || 'Leads imported successfully!');
          } else {
            const numbers = bulkData.mobilesText
              .split(/[\n,]+/)
              .map(num => num.trim())
              .filter(num => num.length > 0);
            
            if (numbers.length === 0) {
              alert('Please select an Excel/CSV file or enter at least one mobile number');
              setLoading(false);
              return;
            }
            
            const payload = numbers.map(num => ({
              mobile: num,
              leadSource: bulkData.leadSource,
              priority: bulkData.priority,
              customerName: `Lead - ${num}`,
              leadStatus: 'New',
              salesStage: 'Inquiry',
            }));
            
            await leadAPI.create(payload);
            alert(`${numbers.length} leads created successfully!`);
          }
        } else {
          await leadAPI.create(formData);
          alert('Lead created successfully!');
        }
      }
      resetForm();
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error saving lead:', error);
      alert(error.response?.data?.message || error.message || 'Failed to save lead');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await leadAPI.delete(id);
      alert('Lead deleted successfully!');
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Failed to delete lead');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedLeads.length} selected leads?`)) return;
    try {
      setLoading(true);
      await leadAPI.bulkDelete(selectedLeads);
      alert(`${selectedLeads.length} leads deleted successfully!`);
      setSelectedLeads([]);
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error deleting bulk leads:', error);
      alert('Failed to delete selected leads');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (lead) => {
    setSelectedLead(lead);
    setFormData({
      customerName: lead.customerName || '',
      mobile: lead.mobile || '',
      email: lead.email || '',
      alternatePhone: lead.alternatePhone || '',
      address: lead.address || { street: '', city: '', state: '', pincode: '' },
      leadSource: lead.leadSource || 'Walk-in',
      leadStatus: lead.leadStatus || 'New',
      salesStage: lead.salesStage || 'Inquiry',
      dealValue: lead.dealValue || 0,
      probability: lead.probability || 0,
      productInterest: lead.productInterest || '',
      requirement: lead.requirement || '',
      budget: lead.budget || { min: 0, max: 0 },
      timeline: lead.timeline || 'Within 1 Month',
      priority: lead.priority || 'Medium',
    });
    setShowModal(true);
  };

  const handleRoleChange = async (roleId) => {
    setAssignmentData({ selectedRole: roleId, selectedEmployee: '' });
    if (roleId) {
      await fetchEmployeesByRole(roleId);
    } else {
      setEmployees([]);
    }
  };

  const handleAssignLead = async () => {
    if (!assignmentData.selectedEmployee) {
      alert('Please select an employee');
      return;
    }

    try {
      if (selectedLeads.length > 1) {
        await leadAPI.bulkAssign(selectedLeads, assignmentData.selectedEmployee);
        alert(`${selectedLeads.length} leads assigned successfully!`);
      } else {
        await leadAPI.assign(selectedLeads[0], assignmentData.selectedEmployee);
        alert('Lead assigned successfully!');
      }
      setShowAssignModal(false);
      setSelectedLeads([]);
      setAssignmentData({ selectedRole: '', selectedEmployee: '' });
      fetchLeads();
    } catch (error) {
      console.error('Error assigning lead:', error);
      alert('Failed to assign lead');
    }
  };

  const handleCheckboxChange = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await leadAPI.updateStatus(leadId, newStatus);
      alert('Lead status updated successfully!');
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      mobile: '',
      email: '',
      alternatePhone: '',
      address: { street: '', city: '', state: '', pincode: '' },
      leadSource: 'Walk-in',
      leadStatus: 'New',
      salesStage: 'Inquiry',
      dealValue: 0,
      probability: 0,
      productInterest: '',
      requirement: '',
      budget: { min: 0, max: 0 },
      timeline: 'Within 1 Month',
      priority: 'Medium',
    });
    setCreationMode('single');
    setBulkFile(null);
    setBulkData({
      mobilesText: '',
      leadSource: 'Call',
      priority: 'Medium',
    });
    setSelectedLead(null);
    setShowModal(false);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'New': 'badge-new',
      'Follow-up': 'badge-followup',
      'Hot': 'badge-hot',
      'Cold': 'badge-cold',
      'Converted': 'badge-converted',
      'Lost': 'badge-lost',
      'Dead': 'badge-dead',
      'Meeting': 'badge-meeting',
      'Not Picked Up': 'badge-not-picked-up',
      'Not Interested': 'badge-not-interested',
      'Interested': 'badge-interested',
      'Switch Off': 'badge-switch-off',
      'Invalid': 'badge-invalid',
      'Busy': 'badge-busy',
      'Call Cut': 'badge-call-cut',
    };
    return `status-badge ${statusClasses[status] || 'badge-default'}`;
  };

  const getStageBadgeClass = (stage) => {
    const stageClasses = {
      'Inquiry': 'stage-inquiry',
      'Demo': 'stage-demo',
      'Negotiation': 'stage-negotiation',
      'Booking': 'stage-booking',
      'Closed': 'stage-closed',
      'Lost': 'stage-lost',
    };
    return `stage-badge ${stageClasses[stage] || 'stage-default'}`;
  };

  return (
    <div className="lead-management">
      <div className="page-header">
        <h1>Lead Management</h1>
        <div className="header-actions">
          {isPrivilegedUser() && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              + Add Lead
            </button>
          )}
          {selectedLeads.length > 0 && isPrivilegedUser() && (
            <button 
              className="btn btn-success" 
              onClick={() => setShowAssignModal(true)}
            >
              Assign Selected ({selectedLeads.length})
            </button>
          )}
          {selectedLeads.length > 0 && isPrivilegedUser() && (
            <button 
              className="btn btn-danger" 
              onClick={handleBulkDelete}
            >
              Delete Selected ({selectedLeads.length})
            </button>
          )}
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && isPrivilegedUser() && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-details">
              <h3>{stats.summary.totalLeads}</h3>
              <p>Total Leads</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🆕</div>
            <div className="stat-details">
              <h3>{stats.summary.newLeads}</h3>
              <p>New Leads</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-details">
              <h3>{stats.summary.hotLeads}</h3>
              <p>Hot Leads</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-details">
              <h3>{stats.summary.convertedLeads}</h3>
              <p>Converted</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-details">
              <h3>{stats.summary.conversionRate}</h3>
              <p>Conversion Rate</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div 
        className="filters-section"
        style={{
          gridTemplateColumns: isPrivilegedUser() ? '2fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr'
        }}
      >
        {isPrivilegedUser() && (
          <input
            type="text"
            placeholder="Search by name, email, or mobile..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="search-input"
          />
        )}
        <select 
          value={filters.status} 
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">All Status</option>
          <option value="New">New</option>
          <option value="Follow-up">Follow-up</option>
          <option value="Hot">Hot</option>
          <option value="Cold">Cold</option>
          <option value="Converted">Converted</option>
          <option value="Lost">Lost</option>
          <option value="Meeting">Meeting</option>
          <option value="Not Picked Up">Not Picked Up</option>
          <option value="Not Interested">Not Interested</option>
          <option value="Interested">Interested</option>
          <option value="Switch Off">Switch Off</option>
          <option value="Invalid">Invalid</option>
          <option value="Busy">Busy</option>
          <option value="Call Cut">Call Cut</option>
        </select>
        <select 
          value={filters.source} 
          onChange={(e) => setFilters({ ...filters, source: e.target.value })}
        >
          <option value="">All Sources</option>
          <option value="Website">Website</option>
          <option value="WhatsApp">WhatsApp</option>
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
          <option value="Call">Call</option>
          <option value="Walk-in">Walk-in</option>
          <option value="Referral">Referral</option>
          <option value="Meta Ads">Meta Ads</option>
          <option value="Google Ads">Google Ads</option>
          <option value="Other">Other</option>
        </select>
        <select 
          value={filters.salesStage} 
          onChange={(e) => setFilters({ ...filters, salesStage: e.target.value })}
        >
          <option value="">All Stages</option>
          <option value="Inquiry">Inquiry</option>
          <option value="Demo">Demo</option>
          <option value="Negotiation">Negotiation</option>
          <option value="Booking">Booking</option>
          <option value="Closed">Closed</option>
        </select>
        {isPrivilegedUser() && (
          <select 
            value={filters.assignmentStatus} 
            onChange={(e) => setFilters({ ...filters, assignmentStatus: e.target.value })}
          >
            <option value="">All Assignment</option>
            <option value="assigned">Assigned</option>
            <option value="unassigned">Unassigned</option>
          </select>
        )}
      </div>

      {/* Leads Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Loading leads...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                {isPrivilegedUser() && (
                  <th>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeads(leads.map(l => l._id));
                        } else {
                          setSelectedLeads([]);
                        }
                      }}
                      checked={selectedLeads.length === leads.length && leads.length > 0}
                    />
                  </th>
                )}
                <th>Lead ID</th>
                <th>Customer Name</th>
                <th>Mobile</th>
                <th>Source</th>
                <th>Status</th>
                <th>Stage</th>
                <th>Deal Value</th>
                <th>Assigned To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={isPrivilegedUser() ? "10" : "9"} className="no-data">No leads found</td>
                </tr>
              ) : (
                leads.map(lead => (
                  <tr key={lead._id}>
                    {isPrivilegedUser() && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead._id)}
                          onChange={() => handleCheckboxChange(lead._id)}
                        />
                      </td>
                    )}
                    <td>{lead.leadId}</td>
                    <td>{lead.customerName}</td>
                    <td>{lead.mobile}</td>
                    <td>{lead.leadSource}</td>
                    <td>
                      <select
                        value={lead.leadStatus}
                        onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                        className={getStatusBadgeClass(lead.leadStatus)}
                      >
                        <option value="New">New</option>
                        <option value="Follow-up">Follow-up</option>
                        <option value="Hot">Hot</option>
                        <option value="Cold">Cold</option>
                        <option value="Converted">Converted</option>
                        <option value="Lost">Lost</option>
                        <option value="Meeting">Meeting</option>
                        <option value="Not Picked Up">Not Picked Up</option>
                        <option value="Not Interested">Not Interested</option>
                        <option value="Interested">Interested</option>
                        <option value="Switch Off">Switch Off</option>
                        <option value="Invalid">Invalid</option>
                        <option value="Busy">Busy</option>
                        <option value="Call Cut">Call Cut</option>
                      </select>
                    </td>
                    <td>
                      <span className={getStageBadgeClass(lead.salesStage)}>
                        {lead.salesStage}
                      </span>
                    </td>
                    <td>₹{lead.dealValue?.toLocaleString() || 0}</td>
                    <td>
                      {lead.assignedTo ? (
                        <span className="assigned-employee">
                          {lead.assignedTo.name}
                        </span>
                      ) : (
                        <span className="unassigned">Unassigned</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-icon btn-view" 
                          onClick={() => {
                            setViewingLead(lead);
                            setShowLeadDetails(true);
                          }}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <CallButton 
                          mobile={lead.mobile} 
                          customerName={lead.customerName} 
                        />
                        {!isPrivilegedUser() && (
                          <NoteButton 
                            leadId={lead._id} 
                            customerName={lead.customerName} 
                            onNoteAdded={fetchLeads} 
                          />
                        )}
                        {isPrivilegedUser() && (
                          <button 
                            className="btn-icon btn-edit" 
                            onClick={() => handleEdit(lead)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                        )}
                        {isPrivilegedUser() && (
                          <button 
                            className="btn-icon btn-assign" 
                            onClick={() => {
                              setSelectedLeads([lead._id]);
                              setShowAssignModal(true);
                            }}
                            title="Assign"
                          >
                            👤
                          </button>
                        )}
                        {isPrivilegedUser() && (
                          <button 
                            className="btn-icon btn-delete" 
                            onClick={() => handleDelete(lead._id)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Lead Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedLead ? 'Edit Lead' : 'Add New Lead'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            {!selectedLead && (
              <div className="modal-tabs">
                <button
                  type="button"
                  className={`modal-tab-btn ${creationMode === 'single' ? 'active' : ''}`}
                  onClick={() => setCreationMode('single')}
                >
                  Single Lead
                </button>
                <button
                  type="button"
                  className={`modal-tab-btn ${creationMode === 'bulk' ? 'active' : ''}`}
                  onClick={() => setCreationMode('bulk')}
                >
                  Bulk Upload (Mobiles)
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="lead-form">
              {creationMode === 'single' ? (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Customer Name *</label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Mobile *</label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Alternate Phone</label>
                    <input
                      type="tel"
                      name="alternatePhone"
                      value={formData.alternatePhone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Lead Source *</label>
                    <select
                      name="leadSource"
                      value={formData.leadSource}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="Website">Website</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="Facebook">Facebook</option>
                      <option value="Instagram">Instagram</option>
                      <option value="Call">Call</option>
                      <option value="Walk-in">Walk-in</option>
                      <option value="Referral">Referral</option>
                      <option value="Meta Ads">Meta Ads</option>
                      <option value="Google Ads">Google Ads</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Lead Status</label>
                    <select
                      name="leadStatus"
                      value={formData.leadStatus}
                      onChange={handleInputChange}
                    >
                      <option value="New">New</option>
                      <option value="Follow-up">Follow-up</option>
                      <option value="Hot">Hot</option>
                      <option value="Cold">Cold</option>
                      <option value="Converted">Converted</option>
                      <option value="Lost">Lost</option>
                      <option value="Meeting">Meeting</option>
                      <option value="Not Picked Up">Not Picked Up</option>
                      <option value="Not Interested">Not Interested</option>
                      <option value="Interested">Interested</option>
                      <option value="Switch Off">Switch Off</option>
                      <option value="Invalid">Invalid</option>
                      <option value="Busy">Busy</option>
                      <option value="Call Cut">Call Cut</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Sales Stage</label>
                    <select
                      name="salesStage"
                      value={formData.salesStage}
                      onChange={handleInputChange}
                    >
                      <option value="Inquiry">Inquiry</option>
                      <option value="Demo">Demo</option>
                      <option value="Negotiation">Negotiation</option>
                      <option value="Booking">Booking</option>
                      <option value="Closed">Closed</option>
                      <option value="Lost">Lost</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Deal Value (₹)</label>
                    <input
                      type="number"
                      name="dealValue"
                      value={formData.dealValue}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Probability (%)</label>
                    <input
                      type="number"
                      name="probability"
                      value={formData.probability}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label>Timeline</label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                    >
                      <option value="Immediate">Immediate</option>
                      <option value="Within 1 Month">Within 1 Month</option>
                      <option value="1-3 Months">1-3 Months</option>
                      <option value="3-6 Months">3-6 Months</option>
                      <option value="More than 6 Months">More than 6 Months</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>Product Interest</label>
                    <input
                      type="text"
                      name="productInterest"
                      value={formData.productInterest}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Requirement</label>
                    <textarea
                      name="requirement"
                      value={formData.requirement}
                      onChange={handleInputChange}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Street Address</label>
                    <input
                      type="text"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Pincode</label>
                    <input
                      type="text"
                      name="address.pincode"
                      value={formData.address.pincode}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              ) : (
                <div className="form-grid">
                  <div className="form-group full-width" style={{ border: '2px dashed #cbd5e0', padding: '20px', borderRadius: '8px', textAlign: 'center', background: '#f8fafc' }}>
                    <label style={{ fontSize: '15px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>📂 Upload Excel / CSV File</label>
                    <input
                      type="file"
                      accept=".xlsx, .xls, .csv"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setBulkFile(file);
                        if (file) {
                          setBulkData(prev => ({ ...prev, mobilesText: '' }));
                        }
                      }}
                      style={{ margin: '10px auto', display: 'block' }}
                    />
                    {bulkFile && (
                      <p style={{ color: '#27ae60', fontSize: '13px', fontWeight: '600', margin: '5px 0 0 0' }}>
                        Selected File: {bulkFile.name}
                      </p>
                    )}
                    <p style={{ fontSize: '11px', color: '#718096', margin: '8px 0 0 0', lineHeight: '1.4' }}>
                      Supports .xlsx, .xls and .csv formats. Column header check is case-insensitive. Must contain a column named "Mobile" or "Phone".
                    </p>
                  </div>

                  {!bulkFile && (
                    <>
                      <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', margin: '10px 0' }}>
                        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }}></div>
                        <span style={{ margin: '0 10px', fontSize: '11px', color: '#a0aec0', fontWeight: 'bold' }}>OR PASTE TEXT</span>
                        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }}></div>
                      </div>

                      <div className="form-group full-width">
                        <label>Mobile Numbers * (Enter comma or newline separated numbers)</label>
                        <textarea
                          placeholder="e.g.&#10;9876543210&#10;9988776655, 9123456780"
                          value={bulkData.mobilesText}
                          onChange={(e) => setBulkData({ ...bulkData, mobilesText: e.target.value })}
                          rows="4"
                          required={!bulkFile}
                        />
                      </div>
                      <div className="form-group">
                        <label>Lead Source *</label>
                        <select
                          value={bulkData.leadSource}
                          onChange={(e) => setBulkData({ ...bulkData, leadSource: e.target.value })}
                          required={!bulkFile}
                        >
                          <option value="Website">Website</option>
                          <option value="WhatsApp">WhatsApp</option>
                          <option value="Facebook">Facebook</option>
                          <option value="Instagram">Instagram</option>
                          <option value="Call">Call</option>
                          <option value="Walk-in">Walk-in</option>
                          <option value="Referral">Referral</option>
                          <option value="Meta Ads">Meta Ads</option>
                          <option value="Google Ads">Google Ads</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Priority</label>
                        <select
                          value={bulkData.priority}
                          onChange={(e) => setBulkData({ ...bulkData, priority: e.target.value })}
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Critical">Critical</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : selectedLead ? 'Update Lead' : 'Create Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Lead Modal */}
      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Lead{selectedLeads.length > 1 ? 's' : ''}</h2>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <div className="assign-form">
              <p className="assign-info">
                Assigning {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''}
              </p>
              <div className="form-group">
                <label>Select Sales Employee *</label>
                <select
                  value={assignmentData.selectedEmployee}
                  onChange={(e) => setAssignmentData({ ...assignmentData, selectedEmployee: e.target.value })}
                >
                  <option value="">-- Select Sales Employee --</option>
                  {employees.map(emp => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} - {emp.employeeId || ''} ({emp.designation || 'Sales'})
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowAssignModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleAssignLead}
                  disabled={!assignmentData.selectedEmployee}
                >
                  Assign Lead{selectedLeads.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Details Modal */}
      {showLeadDetails && viewingLead && (
        <LeadDetails
          lead={viewingLead}
          onClose={() => {
            setShowLeadDetails(false);
            setViewingLead(null);
          }}
          onUpdate={fetchLeads}
        />
      )}
    </div>
  );
};

export default LeadManagement;
